import MaterialPatternInputAdornment from './MaterialPatternInputAdornment';
import MaterialSelector from './MaterialSelector';
import MaterialThumb from './MaterialThumb';
import PatternInput from './PatternInput';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ClearIcon from '@mui/icons-material/Clear';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Popover,
  Tooltip,
} from '@mui/material';
import BoardCanvasWithOverlays from 'boardCanvas/BoardCanvasWithOverlays';
import {
  changePatternMaterial,
  changePatternSize,
  MaterialPatternString,
  parsePatternString,
  toPatternString,
} from 'Pattern';
import { useCallback, useRef, useState } from 'react';
import {
  BoardType,
  Material,
  Renderable,
  RenderableMetadata,
  SneezeGuardState,
  Units,
} from 'Types';

import { createAnonymousBoard } from 'boardClasses/BoardFactory';
import Chevron from 'boardClasses/chevron/Chevron';
import {
  getMaterialsInPattern,
  swapMaterialInPatternString,
} from 'utils/patternUtils';

type Props = {
  id?: string;
  label?: string;
  onChange: (patternString: MaterialPatternString) => void;
  onRemove?: () => void;
  onSelectionChange: (
    selection: null | RenderableMetadata<BoardType.Chevron>
  ) => void;
  previewHeight: number;
  rotate?: number;
  selection: null | { renderableIndex: number };
  units: Units;
  value: MaterialPatternString;
};

function MaterialPatternInput(props: Props) {
  const {
    id,
    label,
    onChange,
    onRemove,
    onSelectionChange,
    previewHeight,
    rotate = 0,
    selection,
    units,
    value,
  } = props;

  const [preDraggedValue, setPreDraggedValue] = useState(value);
  const [isDragging, setIsDragging] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const swapRef = useRef(null);

  const materials = Array.from(getMaterialsInPattern(value))

  const onDrag = useCallback(
    (sgState: SneezeGuardState<BoardType.Chevron>) => {
      onChange(
        adjustPatternForDrag(isDragging ? preDraggedValue : value, sgState)
      );
    },
    [isDragging, preDraggedValue, onChange, value]
  );

  const onDragEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  const onDragStart = useCallback(() => {
    setIsDragging(true);
    setPreDraggedValue(value);
  }, [value]);

  const onSelectedFragmentMaterialChange = (
    material: Material,
    renderable: Renderable<BoardType.Chevron>
  ) => {
    onChange(
      changePatternMaterial(value, renderable.metadata.fragmentIndex, material)
    );
  };

  const onSelectedFragmentSizeChange = (
    size: number,
    dimension: 'height' | 'width',
    renderable: Renderable<BoardType.Chevron>
  ) => {
    onChange(changePatternSize(value, renderable.metadata.fragmentIndex, size));
  };

  const onInputFragmentSelectionChange = (
    startFragmentIndex: number,
    endFragmentIndex: number
  ) => {
    onSelectionChange({
      rowIndex: 0,
      fragmentIndex: startFragmentIndex,
      renderableIndex: startFragmentIndex,
    });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex' }}>
        <PatternInput
          id={id}
          label={label == null ? 'Pattern' : label}
          value={value}
          onChange={onChange}
          onInputFragmentSelectionChange={onInputFragmentSelectionChange}
          InputProps={{
            endAdornment: (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {materials.length === 0 ? null : <IconButton
                  onClick={() => setAnchorEl(swapRef.current)}
                  ref={swapRef}
                >
                  <SwapHorizIcon />
                </IconButton>}

                {anchorEl && (
                  <Popover
                    open={true}
                    anchorEl={anchorEl}
                    onClose={() => setAnchorEl(null)}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right',
                    }}
                    transformOrigin={{
                      horizontal: 'right',
                      vertical: 'top',
                    }}
                  >
                    <Card>
                      <CardHeader title="Swap All"></CardHeader>
                      <CardContent sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
                        {materials.map(
                          (material) => (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                              }}
                            >
                              <MaterialThumb material={material} />
                              <ArrowForwardIcon />
                              <MaterialSelector
                                key={material}
                                onChange={(newMaterial) => {
                                  onChange(
                                    swapMaterialInPatternString(
                                      value,
                                      material,
                                      newMaterial
                                    )
                                  );
                                  setAnchorEl(null)
                                }}
                                value={material}
                              ></MaterialSelector>
                            </Box>
                          )
                        )}
                      </CardContent>
                    </Card>
                  </Popover>
                )}

                <MaterialPatternInputAdornment />
              </Box>
            ),
          }}
        />

        {onRemove && (
          <Box sx={{ display: 'flex', alignItems: 'center', pl: 1 }}>
            <Tooltip placement="top" title="Remove this subpattern">
              <IconButton onClick={onRemove}>
                <ClearIcon />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>

      {parsePatternString(value).some(({ key }) => key) && (
        <Box sx={{ pt: 2 }}>
          <BoardCanvasWithOverlays
            boardInstance={
              createAnonymousBoard(
                Chevron,
                {
                  rowHeight: previewHeight,
                  numRows: 1,
                  thickness: 0,
                  pattern: value,
                  rotate,
                },
                units
              ) as Chevron
            }
            onDrag={onDrag}
            onDragEnd={onDragEnd}
            onDragStart={onDragStart}
            onSelectionChange={onSelectionChange}
            onSelectedFragmentMaterialChange={onSelectedFragmentMaterialChange}
            onSelectedFragmentSizeChange={onSelectedFragmentSizeChange}
            selection={
              selection == null
                ? null
                : {
                    fragmentIndex: selection.renderableIndex,
                    renderableIndex: selection.renderableIndex,
                    rowIndex: 0,
                  }
            }
          ></BoardCanvasWithOverlays>
        </Box>
      )}
    </Box>
  );
}

function adjustPatternForDrag(
  patternString: MaterialPatternString,
  { dragDelta, selection }: SneezeGuardState<BoardType.Chevron>
): MaterialPatternString {
  if (selection == null || dragDelta == null) {
    return patternString;
  }

  const firstSelectedIndex = +selection.fragmentIndex;

  if (firstSelectedIndex == null) {
    return patternString;
  }

  const pattern = parsePatternString(patternString);

  let offsetAcc = 0;
  let indexToSwap = firstSelectedIndex;

  if (dragDelta.x >= 0) {
    while (offsetAcc < dragDelta.x && indexToSwap < pattern.length) {
      offsetAcc += pattern[indexToSwap]?.size ?? 0;
      indexToSwap = Math.min(pattern.length - 1, indexToSwap + 1);
    }
  } else {
    while (offsetAcc > dragDelta.x && indexToSwap >= 1) {
      offsetAcc -= pattern[indexToSwap - 1]?.size ?? 0;
      indexToSwap = Math.max(0, indexToSwap - 1);
    }
  }

  return toPatternString(shiftIndex(pattern, firstSelectedIndex, indexToSwap));
}

function shiftIndex<T>(
  arr: Array<T>,
  pluckIndex: number,
  dropIndex: number
): Array<T> {
  if (
    pluckIndex < 0 ||
    dropIndex < 0 ||
    pluckIndex >= arr.length ||
    dropIndex >= arr.length
  ) {
    return [...arr];
  }

  const result = [...arr];
  const plucked = arr[pluckIndex];
  result.splice(pluckIndex, 1);
  result.splice(dropIndex, 0, plucked);

  return result;
}

export default MaterialPatternInput;
